import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "../lib/api";
import BlogHeader from "../components/BlogHeader";
import { useSeo } from "../lib/seo";

const POST_SEO = {
  "sito-web-manutenzione-mensile": {
    title: "Manutenzione Sito Web Mensile: Perché è Essenziale | Blog Lumora",
    description:
      "Scopri perché la manutenzione mensile del tuo sito web è fondamentale per sicurezza, velocità e posizionamento su Google. Consigli pratici da Lumora.",
  },
  "seo-locale-5-mosse-google": {
    title: "SEO Locale: 5 Strategie per Farti Trovare su Google nel 2026 | Lumora",
    description:
      "Vuoi che la tua attività locale appaia su Google? Ecco 5 mosse pratiche di SEO locale: Google Business Profile, pagine geolocalizzate, velocità mobile e altro.",
  },
};

function renderContent(content) {
  return content.split(/\n\n+/).map((block, i) => {
    if (block.startsWith("## "))
      return (
        <h2 key={i} className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-12 mb-4">
          {block.slice(3)}
        </h2>
      );
    if (block.startsWith("### "))
      return (
        <h3 key={i} className="font-display font-semibold text-xl tracking-tight mt-8 mb-3">
          {block.slice(4)}
        </h3>
      );
    return (
      <p key={i} className="text-neutral-700 leading-relaxed text-base sm:text-lg mb-5">
        {block}
      </p>
    );
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const customSeo = POST_SEO[slug];
  useSeo({
    title: customSeo?.title || (post ? `${post.title} | Blog Lumora` : "Blog Lumora"),
    description: customSeo?.description || post?.excerpt || "Leggi l'articolo sul blog di Lumora.",
    path: `/blog/${slug}`,
    type: "article",
    jsonLd: post
      ? {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          datePublished: post.created_at,
          author: { "@type": "Organization", name: "Lumora" },
          publisher: { "@type": "Organization", name: "Lumora", url: "https://lumorasweb.com" },
        }
      : null,
  });

  useEffect(() => {
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true));
  }, [slug]);

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-[#FAF8F5]">
      <BlogHeader />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14 lg:py-20">
        {!post && !error && (
          <div className="py-24 grid place-items-center">
            <Loader2 className="animate-spin text-neutral-400" />
          </div>
        )}
        {error && (
          <div className="py-24 text-center">
            <h1 className="font-display font-bold text-2xl">Articolo non trovato</h1>
            <Link to="/blog" className="inline-block mt-6 text-sm font-medium text-[#E05638]">
              ← Torna al blog
            </Link>
          </div>
        )}
        {post && (
          <motion.article initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Link
              to="/blog"
              data-testid="blog-post-back"
              className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[#111111] transition-colors"
            >
              <ArrowLeft size={15} /> Tutti gli articoli
            </Link>
            <p className="mono-label text-[#E05638] mt-8">
              {post.created_at ? new Date(post.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : ""}
            </p>
            <h1 data-testid="blog-post-title" className="font-display font-extrabold tracking-tight leading-[1.08] text-3xl sm:text-4xl lg:text-5xl mt-4">
              {post.title}
            </h1>
            {post.cover && (
              <div className="overflow-hidden rounded-2xl border border-black/10 mt-9">
                <img src={post.cover} alt={post.title} className="w-full max-h-96 object-cover" />
              </div>
            )}
            <div data-testid="blog-post-content" className="mt-10">
              {renderContent(post.content || "")}
            </div>
            <div className="mt-14 rounded-2xl bg-[#1E293B] text-white p-8">
              <p className="font-display font-bold text-xl">Vuoi un sito che lavora per te?</p>
              <p className="text-neutral-300 text-sm mt-2 leading-relaxed">
                Creazione, gestione e SEO in un unico abbonamento mensile.
              </p>
              <Link
                to="/"
                data-testid="blog-post-cta"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#E05638] text-white px-6 py-3 text-sm font-semibold hover:bg-[#C8452A] transition-colors"
              >
                Scopri i piani
              </Link>
            </div>
          </motion.article>
        )}
      </main>
    </div>
  );
}
